import {
  REGISTER_FIELDS,
  REGISTER_FORM,
  RESET_FORM,
  RESET_FIELD,
  SYNC_ERRORS,
  UNREGISTER_FIELDS,
  UPDATE_FIELD_META,
  UPDATE_VALUE
} from "./types";
import actions from "./actions";
import Vue from "vue";
import { getDefaultValueForVFFType } from "@/store/utils";

const state = {};

const getters = {
  getField: state => (formName, fieldName) => {
    return state[formName] ? state[formName].fields[fieldName] : {};
  },

  getValue: state => (formName, fieldName) => {
    return state[formName] ? state[formName].values[fieldName] : {};
  },

  isValid: state => formName => {
    const form = state[formName];

    // if not initialized yet
    if (!form) {
      return false;
    }

    let isValid = true;
    const { fields } = form;

    for (let field in fields) {
      if (!isValid) {
        return;
      }

      if (fields[field].meta.errors.length) {
        isValid = false;
      }
    }

    return isValid;
  },

  /**
   * Check is field was touched (focused-blurred)
   * @param state
   * @returns {Function}
   */
  isFieldTouched: state => (formName, fieldName) => {
    const form = state[formName];

    if (!form) {
      return false;
    }

    const field = form.fields[fieldName];

    if (!field || !field.meta) {
      return false;
    }

    return field.meta.focused && field.meta.blurred;
  },

  /**
   * Get values in key/value format
   * @param state
   * @returns {function(*)}
   */
  getValuePairs: state => formName => {
    const form = state[formName];

    if (!form) {
      return null;
    }

    const { values } = form;
    let out = {};

    for (let val of Object.keys(values)) {
      out[val] = values[val];
    }
    return out;
  }
};

const mutations = {
  [REGISTER_FORM](state, { payload }) {
    const { formName } = payload;
    Vue.set(state, formName, { name: formName, fields: {}, values: {} });
  },

  [REGISTER_FIELDS](state, { payload }) {
    const { formName, fields } = payload;
    for (let field of fields) {
      const { elementType, label, name, validate } = field;
      const existingFields = state[formName].fields;

      if (!existingFields[name]) {
        Vue.set(existingFields, name, {
          label,
          type: elementType || "text",
          meta: {
            blurred: false,
            // if field was focused at all
            focused: false,
            // if field is currently focused
            isFocused: false,
            errors: []
          },
          validate: validate || []
        });

        Vue.set(
          state[formName].values,
          name,
          getDefaultValueForVFFType(elementType)
        );
      }
    }
  },

  [UNREGISTER_FIELDS](state, { payload }) {
    const { formName, fieldsNames } = payload;
    const form = state[formName];
    const existingFields = form.fields;

    for (let fieldName of fieldsNames) {
      if (existingFields[fieldName]) {
        Vue.delete(existingFields, fieldName);
        Vue.delete(form.values, fieldName);
      }
    }
  },

  [UPDATE_VALUE](state, { payload }) {
    // todo: fix bug with different the same form
    const { formName, fieldName, value } = payload;
    const form = state[formName];

    if (!form) {
      return;
    }

    // if value doesn't registered
    if (!form.fields[fieldName]) {
      return;
    }

    if (!form.values) {
      Vue.set(form.values, {});
    }

    // settings value
    Vue.set(form.values, fieldName, value);
  },

  [SYNC_ERRORS](state, { payload }) {
    const { formName, fieldName, errors, submitted } = payload;
    const field = state[formName].fields[fieldName];

    if (!field) {
      return;
    }

    Vue.set(field.meta, "errors", Array.from(errors));

    if (submitted) {
      Vue.set(field.meta, "blurred", true);
      Vue.set(field.meta, "focused", true);
    }
  },

  [RESET_FORM](state, { payload }) {
    const { formName } = payload;
    const form = state[formName];

    if (form) {
      const { fields } = form;
      for (let fieldName in fields) {
        const { type } = fields[fieldName];
        // reset errors
        Vue.set(form.fields[fieldName].meta, "errors", []);

        // set initial value
        Vue.set(form.values, fieldName, getDefaultValueForVFFType(type));
      }
    }
  },

  [RESET_FIELD](state, { payload }) {
    const { formName, fieldName } = payload;
    const form = state[formName];

    if (form) {
      const type = form.fields[fieldName].type;
      // reset errors
      Vue.set(form.fields[fieldName].meta, "errors", []);

      // set initial value
      Vue.set(form.values, fieldName, getDefaultValueForVFFType(type));
    }
  },

  [UPDATE_FIELD_META](state, { payload }) {
    const { formName, fieldName, metaKey, metaValue } = payload;
    const form = state[formName];

    // if value doesn't registered
    if (!form.fields[fieldName]) {
      return;
    }

    const field = form.fields[fieldName];
    Vue.set(field.meta, metaKey, metaValue);
  }
};

export default {
  namespaced: true,
  state,
  actions,
  getters,
  mutations
};
